import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Typography, TextField, Button,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Card, CardContent, IconButton,
  FormControl, InputLabel, Select, MenuItem,
  Snackbar, Alert, Backdrop, CircularProgress,
  Divider, Tooltip, useTheme, useMediaQuery,
  Stepper, Step, StepLabel, Chip, Stack,
  Autocomplete, FormControlLabel, Checkbox,
} from '@mui/material';
import {
  Add, Cancel, Save, Edit, Delete,
  ArrowBack, NavigateNext, NavigateBefore,
  InsertChartOutlined, Refresh, AutoAwesome,
  ArrowUpward, ArrowDownward,
} from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import { DynamicBreadcrumbs } from 'components/Breadcrumb';
import apiClient from 'utils/ApiClient';
import { infralytiqsAnalyticsSitePathSegment } from 'utils/infralytiqsAnalyticsPath';
import {
  ReportDefinition, ReportSuiteDetail, ReportQueryDSL, MetricDef, DimensionDef, FilterDef,
  AGG_OPTIONS, OP_OPTIONS, TIME_RANGE_OPTIONS, GRANULARITY_OPTIONS, VISUALIZATION_OPTIONS,
  LAYOUT_WIDTH_OPTIONS, REPORT_MODE_OPTIONS, COUNTER_ICON_OPTIONS, sortReportsByLayout,
  LAYOUT_MAX_W, LAYOUT_MIN_H, LAYOUT_MAX_H, layoutMinWidthForReport,
  FIELD_CATALOG, FieldOption, VisualizationType, Granularity, TimeRangePreset, AggregationFn, FilterOp,
  ReportMode,
  emptyReport,
  DamFolderQuotaEntry,
} from './reportDsl';
import {
  useReportDslMetadata, invalidateReportDslMetadata,
  AggregationFunctionMetadata, FilterOperatorMetadata,
  TimePresetMetadata, GranularityMetadata,
  VisualizationMetadata, IdentityFieldMetadata,
} from './useReportDslMetadata';
import { useFieldValues } from './useFieldValues';
import { withSnapshotCountMetric } from '../Reports/DamStorageQuotaBar';
import {
  REPORTS_STORAGE_KEY_SERVER,
  REPORTS_STORAGE_KEY_SUITE,
} from '../reportsDefaultServer';

const DEFAULT_DAM_FOLDER_QUOTAS: DamFolderQuotaEntry[] = [
  { path: '/content/dam/px/arc', maxStorageTb: 10 },
  { path: '/content/dam/ask-the-lion', maxStorageTb: 5 },
  { path: '/content/dam/stellantis', maxStorageTb: 3 },
  { path: '/content/dam/visa', maxStorageTb: 2 },
  { path: '/content/dam/pxp', maxStorageTb: 6 },
];

interface ModuleConfig {
  _id: string;
  moduleType: string;
  name: string;
  tenantId?: string;
  siteId?: string;
}

const WIZARD_STEPS = ['Basics', 'Metrics', 'Dimensions & Filters', 'Visualization'];

export const SuiteReportEditor: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { configId, suiteId } = useParams<{ configId: string; suiteId: string }>();

  const [parentConfig, setParentConfig] = useState<ModuleConfig | null>(null);
  const [suite, setSuite] = useState<ReportSuiteDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>({
    open: false, message: '', severity: 'info',
  });

  const [wizardOpen, setWizardOpen] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [editingReport, setEditingReport] = useState<ReportDefinition | null>(null);
  const [draft, setDraft] = useState<ReportDefinition>(() => emptyReport('line'));

  // Auto-discovered custom_dimensions / custom_metrics keys from ClickHouse.
  const [customFields, setCustomFields] = useState<{ dimensions: string[]; metrics: string[] } | null>(null);
  const [customFieldsLoading, setCustomFieldsLoading] = useState(false);
  const [customFieldsError, setCustomFieldsError] = useState<string | null>(null);

  const tenantId = parentConfig?.tenantId;
  const siteId = parentConfig?.siteId;

  // Server-driven dropdown catalog for the wizard. When the call fails or
  // hasn't completed yet, every consumer below silently falls back to the
  // static *_OPTIONS constants so the UI never breaks on a metadata blip.
  const {
    metadata: dslMetadata,
    loading: dslMetadataLoading,
    error: dslMetadataError,
    refresh: refreshDslMetadata,
  } = useReportDslMetadata(tenantId, siteId);

  const fetchCustomFields = useCallback(async (opts: { refresh?: boolean } = {}) => {
    if (!tenantId) return;
    const pathSeg = infralytiqsAnalyticsSitePathSegment(siteId);
    setCustomFieldsLoading(true);
    setCustomFieldsError(null);
    try {
      const qs = new URLSearchParams({ lookbackDays: '14' });
      if (opts.refresh) qs.set('refresh', '1');
      const res: any = await apiClient.get(
        `/il/analytics/${encodeURIComponent(tenantId)}/${encodeURIComponent(pathSeg)}/custom-fields?${qs.toString()}`,
      );
      // `apiClient.get()` returns the parsed JSON body verbatim — no
      // axios-style `.data` wrapper. The `/custom-fields` endpoint replies
      // with a flat `{ success, dimensions, metrics, ... }` shape rather
      // than the `{ status, message, data }` envelope the rest of the
      // server uses, so accept both shapes here. Without this guard the
      // discovered custom_dimensions / custom_metrics never make it into
      // `effectiveCatalog`, and the Field dropdowns silently lose all
      // their custom entries.
      const body: any = res?.data ?? res;
      setCustomFields({
        dimensions: Array.isArray(body?.dimensions) ? body.dimensions : [],
        metrics: Array.isArray(body?.metrics) ? body.metrics : [],
      });
    } catch (err: any) {
      setCustomFieldsError(err?.response?.data?.error || err?.message || 'Failed to load custom fields');
    } finally {
      setCustomFieldsLoading(false);
    }
  }, [tenantId, siteId]);

  // Auto-fetch once we know the tenant/site from parentConfig.
  useEffect(() => { fetchCustomFields(); }, [fetchCustomFields]);

  /**
   * Effective field catalog =
   *   (a) the server's standard catalog when the metadata endpoint
   *       responded — its list is grounded in live `system.columns` and
   *       skips columns that don't actually exist on the table;
   *   (b) otherwise the static `FIELD_CATALOG` baked into `reportDsl.ts`
   *       so the wizard still works during a metadata outage;
   * plus the per-tenant custom_dimensions / custom_metrics keys (preferring
   * the metadata response when available, with the older `custom-fields`
   * fetch as a back-stop). Native columns are never duplicated by their
   * `custom_dimensions.<same_key>` shadow.
   */
  const effectiveCatalog: FieldOption[] = useMemo(() => {
    const base: FieldOption[] = dslMetadata?.fieldCatalog?.standard?.length
      ? dslMetadata.fieldCatalog.standard.map((f) => ({
          field: f.field,
          label: f.label,
          group: f.group as FieldOption['group'],
          allowedForDimension: f.allowedForDimension,
          allowedForMetric: f.allowedForMetric,
          isNumeric: f.isNumeric,
        }))
      : [...FIELD_CATALOG];

    const customDims = dslMetadata?.fieldCatalog?.custom?.dimensions ?? customFields?.dimensions ?? [];
    const customMetrics = dslMetadata?.fieldCatalog?.custom?.metrics ?? customFields?.metrics ?? [];

    const existing = new Set(base.map((f) => f.field));
    const existingKeys = new Set(base.map((f) => f.field.split('.').pop()!));

    const dimEntries: FieldOption[] = customDims
      .filter((k) => !existingKeys.has(k))
      .map((k) => ({
        field: `custom_dimensions.${k}`,
        label: `${k}  (custom dim.)`,
        group: 'Custom' as const,
        allowedForDimension: true,
        allowedForMetric: true,
      }))
      .filter((f) => !existing.has(f.field));

    const metricEntries: FieldOption[] = customMetrics.map((k) => ({
      field: `custom_metrics.${k}`,
      label: `${k}  (custom metric)`,
      group: 'Custom' as const,
      allowedForDimension: false,
      allowedForMetric: true,
      isNumeric: true,
    }));

    return [...base, ...dimEntries, ...metricEntries];
  }, [dslMetadata, customFields]);

  /**
   * Effective dropdown lists. Each one prefers `dslMetadata` when present
   * (server-authoritative — adding a new agg fn / op / preset on the
   * server back-end becomes pickable in the wizard automatically) and
   * falls back to the static `*_OPTIONS` constants from `reportDsl.ts`
   * otherwise. We expose them through a single `metaOptions` object so
   * passing them down to the step components stays a one-liner.
   */
  const metaOptions = useMemo(() => ({
    aggregations: (dslMetadata?.aggregationFunctions?.length
      ? dslMetadata.aggregationFunctions
      : AGG_OPTIONS.map<AggregationFunctionMetadata>((a) => ({
          value: a.value, label: a.label, needsField: a.needsField, numericOnly: false,
        }))),
    operators: (dslMetadata?.filterOperators?.length
      ? dslMetadata.filterOperators
      : OP_OPTIONS.map<FilterOperatorMetadata>((o) => ({
          value: o.value, label: o.label, arity: o.arity,
        }))),
    timePresets: (dslMetadata?.timeRangePresets?.length
      ? dslMetadata.timeRangePresets
      : TIME_RANGE_OPTIONS.map<TimePresetMetadata>((t) => ({ value: t.value, label: t.label }))),
    granularities: (dslMetadata?.granularities?.length
      ? dslMetadata.granularities
      : GRANULARITY_OPTIONS.map<GranularityMetadata>((g) => ({ value: g.value, label: g.label }))),
    visualizations: (dslMetadata?.visualizationTypes?.length
      ? dslMetadata.visualizationTypes
      : VISUALIZATION_OPTIONS.map<VisualizationMetadata>((v) => ({
          value: v.value, label: v.label,
          // Best-effort static categorization used only for fallback rendering.
          kind: (v.value === 'realtime' || v.value === 'realtime_bar') ? 'realtime'
              : (v.value === 'geoheat') ? 'geo'
              : (v.value === 'pie' || v.value === 'donut') ? 'distribution'
              : (v.value === 'gauge' || v.value === 'number' || v.value === 'counter' || v.value === 'storage_quota_bar') ? 'single-value'
              : (v.value === 'storage_quota_stacked_bar') ? 'distribution'
              : (v.value === 'table') ? 'tabular'
              : 'time-series',
          aggregatesOverTime: !(v.value === 'line' || v.value === 'area' || v.value === 'bar' || v.value === 'table'),
        }))),
    identityFields: (dslMetadata?.identityFields?.length
      ? dslMetadata.identityFields
      : ([
          { value: 'user_id',      label: 'Distinct user_id' },
          { value: 'anonymous_id', label: 'Distinct anonymous_id' },
          { value: 'session_id',   label: 'Distinct session_id (active sessions)' },
        ] as IdentityFieldMetadata[])),
  }), [dslMetadata]);

  const fetchParentConfig = useCallback(async () => {
    try {
      const res: any = await apiClient.get('/il/settings/module-configs');
      const configs: ModuleConfig[] = Array.isArray(res?.data) ? res.data : (res?.data?.moduleConfigurations ?? []);
      const found = configs.find((c) => c._id === configId);
      if (found) setParentConfig(found);
    } catch { /* silent */ }
  }, [configId]);

  const fetchSuite = useCallback(async () => {
    if (!configId || !suiteId) return;
    setLoading(true);
    try {
      const res: any = await apiClient.get(`/il/settings/module-config/${configId}/report-suites/${suiteId}`);
      setSuite(res?.data ?? null);
    } catch (err: any) {
      setSnackbar({ open: true, message: err?.message || 'Failed to load suite', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [configId, suiteId]);

  useEffect(() => { fetchParentConfig(); }, [fetchParentConfig]);
  useEffect(() => { fetchSuite(); }, [fetchSuite]);

  // Open the Reports dashboard for this suite. The dashboard reads its active
  // ClickHouse server + suite from localStorage on mount, so we seed both keys
  // here before navigating to the (param-less) reports route.
  const handleViewReport = () => {
    if (configId) localStorage.setItem(REPORTS_STORAGE_KEY_SERVER, configId);
    if (configId && suiteId) {
      localStorage.setItem(REPORTS_STORAGE_KEY_SUITE, `${configId}:${suiteId}`);
    }
    navigate('/infralytiqs/reports');
  };

  const openCreate = () => {
    setEditingReport(null);
    const starter = emptyReport('line');
    // Position new reports at the bottom of the dashboard. Using reports.length
    // (rather than max(y)+1) keeps y values dense and predictable when the list
    // has gaps from prior deletions.
    const nextY = suite?.reports?.length ?? 0;
    const prev = starter.layout;
    setDraft({
      ...starter,
      layout: {
        x: prev?.x ?? 0,
        y: nextY,
        w: prev?.w ?? 12,
        h: prev?.h ?? 4,
      },
    });
    setActiveStep(0);
    setWizardOpen(true);
  };

  const openEdit = (report: ReportDefinition) => {
    setEditingReport(report);
    setDraft(JSON.parse(JSON.stringify(report)));
    setActiveStep(0);
    setWizardOpen(true);
  };

  const closeWizard = () => { setWizardOpen(false); setEditingReport(null); };

  // Deep-link support: `?editReport=<id>` (e.g. the Edit button on a report
  // panel) opens that report's editor once the suite has loaded. The param is
  // cleared afterwards so closing/reopening the dialog behaves normally and a
  // browser refresh doesn't force the dialog back open.
  useEffect(() => {
    const editReportId = searchParams.get('editReport');
    if (!editReportId || !suite) return;
    const target = (suite.reports || []).find((r) => r._id === editReportId);
    if (target) openEdit(target);
    const next = new URLSearchParams(searchParams);
    next.delete('editReport');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suite, searchParams]);

  const handleSave = async () => {
    if (!configId || !suiteId) return;
    if (!draft.name.trim()) {
      setSnackbar({ open: true, message: 'Report name is required', severity: 'error' });
      setActiveStep(0);
      return;
    }
    if (!draft.query.metrics?.length) {
      setSnackbar({ open: true, message: 'At least one metric is required', severity: 'error' });
      setActiveStep(1);
      return;
    }

    setLoading(true);
    try {
      const payload = {
        name: draft.name.trim(),
        description: draft.description || '',
        mode: draft.mode || 'dashboard',
        layout: draft.layout,
        query: draft.query,
        visualizations: draft.visualizations,
        interactions: draft.interactions || {},
        rangeComparison: draft.rangeComparison || { enabled: false },
      };
      if (editingReport?._id) {
        await apiClient.patch(
          `/il/settings/module-config/${configId}/report-suites/${suiteId}/reports/${editingReport._id}`,
          payload,
        );
        setSnackbar({ open: true, message: 'Report updated', severity: 'success' });
      } else {
        await apiClient.post(
          `/il/settings/module-config/${configId}/report-suites/${suiteId}/reports`,
          payload,
        );
        setSnackbar({ open: true, message: 'Report created', severity: 'success' });
      }
      closeWizard();
      fetchSuite();
    } catch (err: any) {
      setSnackbar({ open: true, message: err?.message || 'Failed to save report', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (reportId?: string) => {
    if (!reportId) return;
    if (!window.confirm('Delete this report?')) return;
    setLoading(true);
    try {
      await apiClient.delete(
        `/il/settings/module-config/${configId}/report-suites/${suiteId}/reports/${reportId}`,
      );
      setSnackbar({ open: true, message: 'Report deleted', severity: 'success' });
      fetchSuite();
    } catch (err: any) {
      setSnackbar({ open: true, message: err?.message || 'Failed to delete', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const reports = suite?.reports ?? [];

  /**
   * Reports as the dashboard will render them — sorted by `layout.y` with a
   * stable array-index tiebreaker. We show the editor cards in this exact same
   * order so "what you see here is what users see on the Reports page".
   */
  const orderedReports = useMemo(() => sortReportsByLayout(reports), [reports]);

  /**
   * Persist a partial layout patch (e.g. { w: 6 } or { y: 3 }) for one report.
   * We merge with the existing layout on the report so we don't accidentally
   * blow away `x` / `h` (kept around for forward-compat with future grid UIs).
   */
  const patchReportLayout = useCallback(async (
    report: ReportDefinition,
    patch: Partial<NonNullable<ReportDefinition['layout']>>,
  ) => {
    if (!configId || !suiteId || !report._id) return;
    const merged = { ...(report.layout || {}), ...patch };
    try {
      await apiClient.patch(
        `/il/settings/module-config/${configId}/report-suites/${suiteId}/reports/${report._id}`,
        { layout: merged },
      );
    } catch (err: any) {
      setSnackbar({ open: true, message: err?.message || 'Failed to update layout', severity: 'error' });
      throw err;
    }
  }, [configId, suiteId]);

  /**
   * Move a report up or down in the dashboard order. We renumber the entire
   * sorted list to dense 0..N-1 y values after the swap so the order is
   * unambiguous regardless of any pre-existing sparse / duplicate y's.
   *
   * Only the two reports whose y actually changed get a PATCH — the rest are
   * already at their correct y from a previous renumber, so we save round
   * trips and avoid pointless writes.
   */
  const moveReport = useCallback(async (reportId: string, direction: 'up' | 'down') => {
    const idx = orderedReports.findIndex((r) => r._id === reportId);
    if (idx < 0) return;
    const swapWith = direction === 'up' ? idx - 1 : idx + 1;
    if (swapWith < 0 || swapWith >= orderedReports.length) return;

    const next = [...orderedReports];
    [next[idx], next[swapWith]] = [next[swapWith], next[idx]];

    setLoading(true);
    try {
      await Promise.all(
        next.map((r, i) => (
          (r.layout?.y ?? Number.POSITIVE_INFINITY) === i ? null : patchReportLayout(r, { y: i })
        )).filter(Boolean) as Promise<void>[],
      );
      await fetchSuite();
    } catch { /* snackbar already shown */ }
    finally { setLoading(false); }
  }, [orderedReports, patchReportLayout, fetchSuite]);

  const setReportWidth = useCallback(async (report: ReportDefinition, w: number) => {
    setLoading(true);
    try {
      await patchReportLayout(report, { w });
      await fetchSuite();
    } catch { /* snackbar already shown */ }
    finally { setLoading(false); }
  }, [patchReportLayout, fetchSuite]);

  const setReportHeight = useCallback(async (report: ReportDefinition, h: number) => {
    setLoading(true);
    try {
      await patchReportLayout(report, { h });
      await fetchSuite();
    } catch { /* snackbar already shown */ }
    finally { setLoading(false); }
  }, [patchReportLayout, fetchSuite]);

  return (
    <Box sx={{ pl: { xs: 1.25, md: 2.5 }, pt: 1, width: '100%', mb: 3 }}>
      {loading && (
        <Backdrop sx={(t: any) => ({ color: t.palette.secondary.main, zIndex: t.zIndex.drawer + 1 })} open={loading}>
          <CircularProgress color="inherit" />
        </Backdrop>
      )}

      <DynamicBreadcrumbs items={[
        { itemPath: '/dashboard', itemLabel: 'Home' },
        { itemPath: '/infralytiqs/settings', itemLabel: 'Infralytiqs Settings' },
        { itemPath: `/infralytiqs/settings/${configId}`, itemLabel: parentConfig?.name || 'Configuration' },
        { itemPath: '', itemLabel: suite?.name || 'Suite' },
      ]} />

      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title="Back to Report Suites">
            <IconButton size="small" onClick={() => navigate(`/infralytiqs/settings/${configId}`)}>
              <ArrowBack />
            </IconButton>
          </Tooltip>
          <Box>
            <Typography variant="h5" fontWeight={700} sx={{ mb: 0 }}>
              {suite?.name || 'Report Suite'}
            </Typography>
            {suite?.description && (
              <Typography variant="body2" color="text.secondary">{suite.description}</Typography>
            )}
          </Box>
        </Box>
        {!isMobile && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<InsertChartOutlined />}
              onClick={handleViewReport}
            >
              View Report
            </Button>
            <Button variant="contained" color="secondary" startIcon={<Add />} onClick={openCreate}>
              New Report
            </Button>
          </Box>
        )}
      </Box>

      {isMobile && (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, mb: 2 }}>
          <Button fullWidth variant="outlined" color="secondary" startIcon={<InsertChartOutlined />} onClick={handleViewReport}
            sx={{ maxWidth: 480, py: 1.25, borderWidth: 2, textTransform: 'uppercase', fontWeight: 700 }}>
            View Report
          </Button>
          <Button fullWidth variant="outlined" color="secondary" startIcon={<Add />} onClick={openCreate}
            sx={{ maxWidth: 480, py: 1.25, borderWidth: 2, textTransform: 'uppercase', fontWeight: 700 }}>
            New Report
          </Button>
        </Box>
      )}

      {/* Reports grid */}
      {reports.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <InsertChartOutlined sx={{ fontSize: 64, color: theme.palette.action.disabled, mb: 2 }} />
          <Typography variant="h6" color="text.secondary">No reports yet</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Add your first report (e.g. "Unique Users — last 30 days") to this suite.
          </Typography>
          <Button variant="contained" color="secondary" startIcon={<Add />} onClick={openCreate}>
            Create Report
          </Button>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2.5 }}>
          {orderedReports.map((r, displayIdx) => {
            const widthVal = r.layout?.w ?? 12;
            const heightVal = r.layout?.h ?? 4;
            const minW = layoutMinWidthForReport(r);
            const yVal = typeof r.layout?.y === 'number' ? r.layout!.y! : displayIdx;
            const isFirst = displayIdx === 0;
            const isLast = displayIdx === orderedReports.length - 1;
            return (
              <Card key={r._id} variant="outlined"
                sx={{ width: { xs: '100%', sm: 340 }, borderRadius: 2, display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flex: 1, pb: 1 }}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1, flexWrap: 'wrap' }}>
                    <Chip
                      size="small"
                      label={`#${displayIdx + 1}`}
                      color="secondary"
                      sx={{ fontWeight: 600 }}
                    />
                    <Chip
                      size="small"
                      label={r.visualizations?.[0]?.type || 'n/a'}
                      color="secondary"
                      variant="outlined"
                    />
                    <Tooltip title={`Width: ${widthVal}/12 cols · Height: ${heightVal} rows`}>
                      <Chip
                        size="small"
                        label={`${widthVal}/12 · ${heightVal}r`}
                        variant="outlined"
                      />
                    </Tooltip>
                    {r.mode === 'drilldown' && (
                      <Chip size="small" label="drilldown" variant="outlined" />
                    )}
                  </Stack>
                  <Typography variant="subtitle1" fontWeight={600} noWrap>{r.name}</Typography>
                  {r.description && (
                    <Typography variant="body2" color="text.secondary"
                      sx={{ mt: 0.5, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                      {r.description}
                    </Typography>
                  )}
                  <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 1 }}>
                    {r.query.metrics?.length ?? 0} metric(s) • {r.query.dimensions?.length ?? 0} dimension(s) •{' '}
                    {r.query.timeRange?.preset || 'custom range'} • order #{yVal}
                  </Typography>

                  {/*
                    Inline layout quick-edit. Width / Height steppers cover the
                    full 3-12 / 3-40 ranges (matches the drag-resize handle on
                    the Reports dashboard); the up/down arrows below remain the
                    primary "reorder" affordance for non-drag-capable contexts.
                    Drilldown reports are not laid out on the main dashboard,
                    but they do flow on the user drill-down page so we expose
                    the same controls there too.
                  */}
                  <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
                    <TextField
                      size="small"
                      type="number"
                      label="Width"
                      value={widthVal}
                      onChange={(e) => {
                        const n = parseInt(e.target.value, 10);
                        if (!Number.isFinite(n)) return;
                        const clamped = Math.min(Math.max(n, minW), LAYOUT_MAX_W);
                        if (clamped !== widthVal) setReportWidth(r, clamped);
                      }}
                      inputProps={{ min: minW, max: LAYOUT_MAX_W, step: 1 }}
                      sx={{ flex: 1 }}
                    />
                    <TextField
                      size="small"
                      type="number"
                      label="Height"
                      value={heightVal}
                      onChange={(e) => {
                        const n = parseInt(e.target.value, 10);
                        if (!Number.isFinite(n)) return;
                        const clamped = Math.min(Math.max(n, LAYOUT_MIN_H), LAYOUT_MAX_H);
                        if (clamped !== heightVal) setReportHeight(r, clamped);
                      }}
                      inputProps={{ min: LAYOUT_MIN_H, max: LAYOUT_MAX_H, step: 1 }}
                      sx={{ flex: 1 }}
                    />
                  </Box>
                </CardContent>
                <Divider />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 1, py: 0.5, gap: 0.5 }}>
                  <Box sx={{ display: 'flex', gap: 0.25 }}>
                    <Tooltip title="Move up (earlier in dashboard)">
                      <span>
                        <IconButton size="small" disabled={isFirst} onClick={() => moveReport(r._id!, 'up')}>
                          <ArrowUpward fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Move down (later in dashboard)">
                      <span>
                        <IconButton size="small" disabled={isLast} onClick={() => moveReport(r._id!, 'down')}>
                          <ArrowDownward fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                  {/*
                    Match the contained-button style used by the suite tiles
                    on /#/infralytiqs/settings/:configId so the editor cards
                    feel like they belong to the same family of controls.
                  */}
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <Tooltip title="Edit">
                      <Button
                        color="secondary"
                        variant="contained"
                        size="small"
                        onClick={() => openEdit(r)}
                        sx={{ borderRadius: 1, textTransform: 'none', px: 1.25, minWidth: 36 }}
                        aria-label="Edit report"
                      >
                        <Edit sx={{ fontSize: 16 }} />
                      </Button>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <Button
                        color="error"
                        variant="contained"
                        size="small"
                        onClick={() => handleDelete(r._id)}
                        sx={{ borderRadius: 1, textTransform: 'none', px: 1.25, minWidth: 36 }}
                        aria-label="Delete report"
                      >
                        <Delete sx={{ fontSize: 16 }} />
                      </Button>
                    </Tooltip>
                  </Box>
                </Box>
              </Card>
            );
          })}
        </Box>
      )}

      {/* Wizard dialog */}
      <Dialog open={wizardOpen} onClose={closeWizard} maxWidth="md" fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle
          component="div"
          sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}
        >
          <Typography variant="h6" component="span" fontWeight={600}>
            {editingReport ? 'Edit Report' : 'Create Report'}
          </Typography>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Tooltip
              title={
                customFieldsError || dslMetadataError
                  ? `Auto-discovery failed: ${customFieldsError || dslMetadataError}`
                  : customFields
                    ? `Discovered ${customFields.dimensions.length} custom dim · ${customFields.metrics.length} custom metric (last 14d). `
                      + `Click to refresh fields + DSL metadata (aggregations, ops, presets, viz types).`
                    : 'Refresh fields + DSL metadata from the server'
              }
            >
              <span>
                <IconButton
                  size="small"
                  onClick={() => {
                    // Refresh both the legacy custom-fields call and the new
                    // server-driven DSL metadata in one click — they share
                    // the same "tell me what's available right now" intent
                    // from the user's perspective.
                    if (tenantId) invalidateReportDslMetadata(tenantId, siteId);
                    fetchCustomFields({ refresh: true });
                    refreshDslMetadata();
                  }}
                  disabled={customFieldsLoading || dslMetadataLoading || !tenantId}
                  color={(customFieldsError || dslMetadataError) ? 'error' : 'default'}
                >
                  {(customFieldsLoading || dslMetadataLoading) ? <CircularProgress size={18} /> : <Refresh fontSize="small" />}
                </IconButton>
              </span>
            </Tooltip>
            <IconButton onClick={closeWizard} size="small"><CloseIcon /></IconButton>
          </Stack>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 3 }}>
          <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 3 }}>
            {WIZARD_STEPS.map((label) => (
              <Step key={label}><StepLabel>{label}</StepLabel></Step>
            ))}
          </Stepper>

          {customFields && (customFields.dimensions.length > 0 || customFields.metrics.length > 0) && (
            <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <AutoAwesome fontSize="small" color="secondary" />
              <Typography variant="caption" color="text.secondary">
                Auto-discovered fields (last 14d):
              </Typography>
              {customFields.dimensions.slice(0, 6).map((k) => (
                <Chip key={`dim-${k}`} size="small" label={k} variant="outlined" color="secondary" />
              ))}
              {customFields.metrics.slice(0, 6).map((k) => (
                <Chip key={`met-${k}`} size="small" label={`${k} #`} variant="outlined" />
              ))}
              {(customFields.dimensions.length + customFields.metrics.length) > 12 && (
                <Typography variant="caption" color="text.disabled">
                  +{(customFields.dimensions.length + customFields.metrics.length) - 12} more in dropdowns
                </Typography>
              )}
            </Box>
          )}

          {activeStep === 0 && <BasicsStep draft={draft} setDraft={setDraft} />}
          {activeStep === 1 && <MetricsStep draft={draft} setDraft={setDraft} catalog={effectiveCatalog} meta={metaOptions} tenantId={tenantId} siteId={siteId} />}
          {activeStep === 2 && <DimensionsFiltersStep draft={draft} setDraft={setDraft} catalog={effectiveCatalog} meta={metaOptions} tenantId={tenantId} siteId={siteId} />}
          {activeStep === 3 && <VisualizationStep draft={draft} setDraft={setDraft} reports={reports} catalog={effectiveCatalog} meta={metaOptions} tenantId={tenantId} siteId={siteId} />}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={closeWizard} startIcon={<Cancel />} variant="outlined" color="secondary">
            Cancel
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button
            disabled={activeStep === 0}
            onClick={() => setActiveStep((s) => s - 1)}
            startIcon={<NavigateBefore />}
            variant="outlined"
            color="secondary"
          >
            Back
          </Button>
          {activeStep < WIZARD_STEPS.length - 1 ? (
            <Button
              variant="contained"
              color="secondary"
              endIcon={<NavigateNext />}
              onClick={() => setActiveStep((s) => s + 1)}
            >
              Next
            </Button>
          ) : (
            <Button variant="contained" color="success" startIcon={<Save />} onClick={handleSave}>
              {editingReport ? 'Update' : 'Create'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
          severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

// ─── Step 1: Basics ───────────────────────────────────────────────────────

interface StepProps {
  draft: ReportDefinition;
  setDraft: React.Dispatch<React.SetStateAction<ReportDefinition>>;
}

const BasicsStep: React.FC<StepProps> = ({ draft, setDraft }) => {
  const layout = draft.layout || { x: 0, y: 0, w: 12, h: 4 };
  const widthVal = typeof layout.w === 'number' ? layout.w : 12;
  const heightVal = typeof layout.h === 'number' ? layout.h : 4;
  const yVal = typeof layout.y === 'number' ? layout.y : 0;

  const updateLayout = (patch: Partial<NonNullable<ReportDefinition['layout']>>) =>
    setDraft((d) => ({ ...d, layout: { ...(d.layout || { x: 0, y: 0, w: 12, h: 4 }), ...patch } }));

  /** Clamp helper used by both number steppers below. */
  const clampInt = (raw: string, min: number, max: number, fallback: number): number => {
    const n = parseInt(raw, 10);
    if (!Number.isFinite(n)) return fallback;
    return Math.min(Math.max(n, min), max);
  };

  const widthPreset = LAYOUT_WIDTH_OPTIONS.find((o) => o.value === widthVal);
  const minW = layoutMinWidthForReport(draft);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      <TextField
        label="Report Name"
        fullWidth required
        value={draft.name}
        onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
        placeholder="e.g. Unique Users — last 30 days"
      />
      <TextField
        label="Description"
        fullWidth multiline minRows={2}
        value={draft.description || ''}
        onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))}
        placeholder="What question does this report answer?"
      />
      <FormControl fullWidth size="small">
        <InputLabel>Mode</InputLabel>
        <Select
          label="Mode"
          value={draft.mode || 'dashboard'}
          onChange={(e) => setDraft((d) => ({ ...d, mode: e.target.value as ReportMode }))}
        >
          {REPORT_MODE_OPTIONS.map((o) => (
            <MenuItem key={o.value} value={o.value}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <Typography variant="body2" component="span" fontWeight={600}>{o.label}</Typography>
                <Typography variant="caption" component="span" color="text.secondary">
                  {o.description}
                </Typography>
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Divider>Layout on the Reports dashboard</Divider>

      <Typography variant="caption" color="text.secondary" sx={{ mt: -1 }}>
        These values are also updated automatically when an admin drags the
        corner handle to resize, or grabs the top-left handle to reorder a
        panel on the Reports dashboard.
      </Typography>

      <Box sx={{ display: 'flex', gap: 2, flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
        {/*
          Width is now a freeform stepper (3-12). The drag-resize feature
          can produce any integer in that range, so a 5-option dropdown
          would silently drop values like 5/7/9/10/11. The helper text
          surfaces the preset name when the value matches one.
        */}
        <TextField
          size="small"
          type="number"
          label="Panel Width (columns)"
          helperText={
            widthPreset
              ? `${widthPreset.label} — ${minW}–${LAYOUT_MAX_W} of a 12-column grid`
              : `${widthVal} / 12 columns — ${minW}–${LAYOUT_MAX_W} allowed`
          }
          value={widthVal}
          onChange={(e) => updateLayout({ w: clampInt(e.target.value, minW, LAYOUT_MAX_W, 12) })}
          inputProps={{ min: minW, max: LAYOUT_MAX_W, step: 1 }}
          sx={{ flex: 1, minWidth: 180 }}
        />

        <TextField
          size="small"
          type="number"
          label="Panel Height (rows)"
          helperText={`${LAYOUT_MIN_H}–${LAYOUT_MAX_H} rows · 1 row ≈ 80 px`}
          value={heightVal}
          onChange={(e) => updateLayout({ h: clampInt(e.target.value, LAYOUT_MIN_H, LAYOUT_MAX_H, 4) })}
          inputProps={{ min: LAYOUT_MIN_H, max: LAYOUT_MAX_H, step: 1 }}
          sx={{ flex: 1, minWidth: 160 }}
        />

        <TextField
          size="small"
          type="number"
          label="Display order"
          helperText="Lower numbers render first. Drag-to-reorder rewrites these."
          value={yVal}
          onChange={(e) => {
            const n = parseInt(e.target.value, 10);
            updateLayout({ y: Number.isFinite(n) ? Math.max(0, n) : 0 });
          }}
          inputProps={{ min: 0, step: 1 }}
          sx={{ flex: 1, minWidth: 160 }}
        />
      </Box>
    </Box>
  );
};

// ─── Step 2: Metrics ──────────────────────────────────────────────────────

interface MetaOptions {
  aggregations: AggregationFunctionMetadata[];
  operators: FilterOperatorMetadata[];
  timePresets: TimePresetMetadata[];
  granularities: GranularityMetadata[];
  visualizations: VisualizationMetadata[];
  identityFields: IdentityFieldMetadata[];
}

interface StepWithCatalogProps extends StepProps {
  catalog: FieldOption[];
  /**
   * Server-driven option lists for the wizard's dropdowns. Each entry is
   * already pre-merged with a static fallback in `SuiteReportEditor`, so
   * step components can render them unconditionally without checking for
   * `null` / `loading`.
   */
  meta: MetaOptions;
  /**
   * Tenant + site of the parent module config — passed down so the
   * Filter Value picker can fetch distinct values from
   * `/il/analytics/.../field-values`. Both undefined while
   * `parentConfig` hasn't resolved yet; the value picker degrades to a
   * plain text box in that window.
   */
  tenantId?: string;
  siteId?: string;
}

/**
 * Returns `options` augmented with a synthetic entry that mirrors `savedField`
 * when the catalog doesn't already list it.
 *
 * Why we need this:
 *   • The wizard's `<Select value={f.field} />` is rendered synchronously when
 *     the dialog opens, but the field catalog (custom_dimensions discovery,
 *     server-side `system.columns` schema) loads asynchronously a moment
 *     later. Without a fallback, MUI silently collapses the Select to an
 *     empty box because the saved value matches no `<MenuItem>`.
 *   • Even after the catalog loads, custom-dimension keys that aged out of
 *     the 14-day discovery window — or were missed by a transient stale
 *     cache — would otherwise erase the user's saved filter from the UI.
 *
 * The synthesized option is purely a display-time fallback: the real catalog
 * is still the authoritative source for any new picks the user makes. Custom
 * dim/metric fields ("custom_dimensions.<key>") get a clear "(custom dim.)"
 * tag so the user can tell when a saved value isn't currently in the live
 * catalog (e.g. because no events with that key landed in the last 14 days).
 */
const ensureSavedFieldOption = (
  options: FieldOption[],
  savedField: string | undefined | null,
): FieldOption[] => {
  if (!savedField || savedField === '*') return options;
  if (options.some((o) => o.field === savedField)) return options;

  let label = savedField;
  let group: FieldOption['group'] = 'Event';
  if (savedField.startsWith('custom_dimensions.')) {
    const k = savedField.slice('custom_dimensions.'.length);
    label = `${k}  (custom dim.)`;
    group = 'Custom';
  } else if (savedField.startsWith('custom_metrics.')) {
    const k = savedField.slice('custom_metrics.'.length);
    label = `${k}  (custom metric)`;
    group = 'Custom';
  }

  return [
    ...options,
    {
      field: savedField,
      label,
      group,
      // Don't constrain — the saved value already passed compiler validation
      // when the report was first authored, so allow it everywhere it might
      // currently sit in the wizard.
      allowedForDimension: true,
      allowedForMetric: true,
    },
  ];
};

// ─── Filter value picker (per-row) ────────────────────────────────────────

interface FilterValuePickerProps {
  tenantId?: string;
  siteId?: string;
  field: string;
  arity: 'none' | 'single' | 'multi';
  value: FilterDef['value'];
  onChange: (value: FilterDef['value']) => void;
}

/**
 * Smart Value input for a Filter row. Replaces the previous blind
 * `<TextField>` / `<Autocomplete options={[]}/>` with a typeahead seeded
 * by the most-frequent distinct values for `field` over the last 30
 * days, fetched from `/il/analytics/.../field-values`.
 *
 * Behaviour by arity:
 *   • `single`  — Autocomplete with `freeSolo`, single value, options
 *                 from ClickHouse. User can still type any value.
 *   • `multi`   — same shape but `multiple`, for `in` / `nin` ops.
 *   • `none`    — renders nothing (e.g. for `exists` / `not_exists`).
 *
 * Custom-dimension fields whose values are JSON-encoded arrays
 * (e.g. `custom_dimensions.DAM = ["ARC-DAM","PX-DAM",…]`) are unpacked
 * by the server into per-element rows, so the dropdown surfaces each
 * tag as its own pickable option — the user no longer has to remember
 * the exact strings.
 */
const FilterValuePicker: React.FC<FilterValuePickerProps> = ({
  tenantId, siteId, field, arity, value, onChange,
}) => {
  const enabled = arity !== 'none' && !!field && field !== '*';
  const { values, truncated, loading, error } = useFieldValues(tenantId, siteId, field, enabled);

  // The Autocomplete needs `string[]` for `options`. We append the
  // `(N)` count via `getOptionLabel` so users can spot the most-common
  // values at a glance without bloating the option `value` itself.
  const optionStrings = values.map((v) => v.value);
  const labelByValue = new Map(values.map((v) => [v.value, `${v.value}  ·  ${v.count.toLocaleString()}`]));
  const renderLabel = (v: string) => labelByValue.get(v) || v;

  if (arity === 'none') return null;

  const helper = error
    ? `Could not load suggestions: ${error}`
    : (loading
      ? 'Loading suggestions…'
      : (values.length > 0
        ? (truncated
          ? `${values.length}+ recent values shown — type to add another`
          : `${values.length} recent values from ClickHouse — or type your own`)
        : 'No suggestions yet — type a value'));

  if (arity === 'single') {
    const current = typeof value === 'string' || typeof value === 'number' ? String(value) : '';
    return (
      <Autocomplete
        size="small"
        freeSolo
        options={optionStrings}
        value={current}
        onChange={(_e, v) => onChange((v ?? '') as string)}
        onInputChange={(_e, v, reason) => {
          // Keep the saved value in sync with what the user is typing,
          // so they don't have to press Enter for free-form values.
          if (reason === 'input') onChange(v);
        }}
        getOptionLabel={(o) => renderLabel(o as string)}
        sx={{ flex: 1, minWidth: 200 }}
        loading={loading}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Value"
            helperText={helper}
            error={!!error}
          />
        )}
      />
    );
  }

  // arity === 'multi'
  const arr = Array.isArray(value) ? value.map(String) : [];
  return (
    <Autocomplete
      multiple
      freeSolo
      size="small"
      options={optionStrings}
      value={arr}
      onChange={(_e, v) => onChange(v as string[])}
      getOptionLabel={(o) => renderLabel(o as string)}
      sx={{ flex: 1, minWidth: 240 }}
      loading={loading}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Values (press Enter)"
          helperText={helper}
          error={!!error}
        />
      )}
    />
  );
};

const MetricsStep: React.FC<StepWithCatalogProps> = ({ draft, setDraft, catalog, meta }) => {
  const metrics = draft.query.metrics || [];

  const updateMetric = (idx: number, patch: Partial<MetricDef>) => {
    setDraft((d) => ({
      ...d,
      query: { ...d.query, metrics: metrics.map((m, i) => (i === idx ? { ...m, ...patch } : m)) },
    }));
  };
  const addMetric = () => {
    setDraft((d) => ({ ...d, query: { ...d.query, metrics: [...metrics, { field: '*', fn: 'count', label: '' }] } }));
  };
  const removeMetric = (idx: number) => {
    if (metrics.length <= 1) return;
    setDraft((d) => ({ ...d, query: { ...d.query, metrics: metrics.filter((_, i) => i !== idx) } }));
  };

  const aggOptions = meta.aggregations;
  // For numeric-only aggregations (sum/avg/min/max) restrict the Field
  // dropdown to numeric columns when the metadata told us which are
  // numeric — keeps the wizard from offering nonsensical picks like
  // "sum of event_type". Falls back to "all metric-eligible fields" when
  // we don't have type info (older fallback path).
  const baseMetricFieldOptions = catalog.filter((f) => f.allowedForMetric);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="body2" color="text.secondary">
        A metric is "what to measure" — e.g. <code>count of events</code>, <code>unique users</code>, or{' '}
        <code>sum of time_spent</code>.
      </Typography>

      {metrics.map((m, idx) => {
        const aggOpt = aggOptions.find((a) => a.value === m.fn);
        const numericOnly = !!aggOpt?.numericOnly;
        const metricFieldOptions = numericOnly
          ? baseMetricFieldOptions.filter((f) => f.isNumeric)
          : baseMetricFieldOptions;
        return (
          <Box key={idx}
            sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: { xs: 'wrap', md: 'nowrap' },
                  p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Aggregation</InputLabel>
              <Select
                label="Aggregation"
                value={m.fn}
                onChange={(e) => updateMetric(idx, { fn: e.target.value as AggregationFn })}
              >
                {aggOptions.map((a) => (
                  <MenuItem key={a.value} value={a.value}>{a.label}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 200, flex: 1 }} disabled={!aggOpt?.needsField}>
              <InputLabel>Field</InputLabel>
              <Select
                label="Field"
                value={aggOpt?.needsField ? m.field : '*'}
                onChange={(e) => updateMetric(idx, { field: e.target.value as string })}
              >
                {ensureSavedFieldOption(
                  metricFieldOptions,
                  aggOpt?.needsField ? m.field : undefined,
                ).map((f) => (
                  <MenuItem key={f.field} value={f.field}>{f.label}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              size="small"
              label="Display label"
              value={m.label || ''}
              onChange={(e) => updateMetric(idx, { label: e.target.value })}
              sx={{ flex: 1, minWidth: 160 }}
            />

            <Tooltip title="Remove">
              <span>
                <IconButton
                  color="error"
                  size="small"
                  onClick={() => removeMetric(idx)}
                  disabled={metrics.length <= 1}
                >
                  <Delete fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        );
      })}

      <Button variant="outlined" color="secondary" startIcon={<Add />} onClick={addMetric} sx={{ alignSelf: 'flex-start' }}>
        Add Metric
      </Button>
    </Box>
  );
};

// ─── Step 3: Dimensions & Filters ─────────────────────────────────────────

const DimensionsFiltersStep: React.FC<StepWithCatalogProps> = ({ draft, setDraft, catalog, meta, tenantId, siteId }) => {
  const dimensions = draft.query.dimensions || [];
  const filters = draft.query.filters || [];
  const timeRange = draft.query.timeRange || {};

  const dimensionFieldOptions = catalog.filter((f) => f.allowedForDimension);

  const addDimension = () => setDraft((d) => ({
    ...d,
    query: { ...d.query, dimensions: [...dimensions, { field: 'event_type', label: '' }] },
  }));
  const updateDim = (idx: number, patch: Partial<DimensionDef>) => setDraft((d) => ({
    ...d,
    query: { ...d.query, dimensions: dimensions.map((x, i) => (i === idx ? { ...x, ...patch } : x)) },
  }));
  const removeDim = (idx: number) => setDraft((d) => ({
    ...d,
    query: { ...d.query, dimensions: dimensions.filter((_, i) => i !== idx) },
  }));

  const addFilter = () => setDraft((d) => ({
    ...d,
    query: { ...d.query, filters: [...filters, { field: 'event_type', op: 'eq', value: '' }] },
  }));
  const updateFilter = (idx: number, patch: Partial<FilterDef>) => setDraft((d) => ({
    ...d,
    query: { ...d.query, filters: filters.map((f, i) => (i === idx ? { ...f, ...patch } : f)) },
  }));
  const removeFilter = (idx: number) => setDraft((d) => ({
    ...d,
    query: { ...d.query, filters: filters.filter((_, i) => i !== idx) },
  }));

  const setTimeRange = (patch: Partial<NonNullable<ReportQueryDSL['timeRange']>>) =>
    setDraft((d) => ({ ...d, query: { ...d.query, timeRange: { ...(d.query.timeRange || {}), ...patch } } }));

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Time range */}
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>Time Range</Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Preset</InputLabel>
            <Select
              label="Preset"
              value={timeRange.preset || 'last_30_days'}
              onChange={(e) => setTimeRange({ preset: e.target.value as TimeRangePreset, start: undefined, end: undefined })}
            >
              {meta.timePresets.map((o) => (
                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Granularity</InputLabel>
            <Select
              label="Granularity"
              value={timeRange.granularity || ''}
              onChange={(e) => setTimeRange({ granularity: (e.target.value || undefined) as Granularity | undefined })}
            >
              <MenuItem value="">— None (aggregate) —</MenuItem>
              {meta.granularities.map((o) => (
                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
          Pick a granularity for time-series charts (line/area/bar over time). Leave empty for pie / table / single-number views.
        </Typography>
      </Box>

      <Divider />

      {/* Dimensions */}
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="subtitle2">Dimensions (group by)</Typography>
          <Button
            size="small"
            variant="outlined"
            color="secondary"
            startIcon={<Add />}
            onClick={addDimension}
          >
            Add
          </Button>
        </Box>
        {dimensions.length === 0 ? (
          <Typography variant="caption" color="text.secondary">
            None — the metric is reported as a single number (per time bucket if granularity is set).
          </Typography>
        ) : (
          dimensions.map((dim, idx) => (
            <Box key={idx} sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
              <FormControl size="small" sx={{ minWidth: 200, flex: 1 }}>
                <InputLabel>Field</InputLabel>
                <Select
                  label="Field"
                  value={dim.field}
                  onChange={(e) => updateDim(idx, { field: e.target.value as string })}
                >
                  {ensureSavedFieldOption(dimensionFieldOptions, dim.field).map((f) => (
                    <MenuItem key={f.field} value={f.field}>{f.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                size="small" label="Label"
                value={dim.label || ''}
                onChange={(e) => updateDim(idx, { label: e.target.value })}
                sx={{ flex: 1, minWidth: 160 }}
              />
              <IconButton color="error" size="small" onClick={() => removeDim(idx)}>
                <Delete fontSize="small" />
              </IconButton>
            </Box>
          ))
        )}
      </Box>

      <Divider />

      {/* Filters */}
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="subtitle2">Filters</Typography>
          <Button
            size="small"
            variant="outlined"
            color="secondary"
            startIcon={<Add />}
            onClick={addFilter}
          >
            Add
          </Button>
        </Box>
        {filters.length === 0 ? (
          <Typography variant="caption" color="text.secondary">No filters — all events in the time range are included.</Typography>
        ) : (
          filters.map((f, idx) => {
            const opMeta = meta.operators.find((o) => o.value === f.op);
            return (
              <Box key={idx} sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
                <FormControl size="small" sx={{ minWidth: 160, flex: 1 }}>
                  <InputLabel>Field</InputLabel>
                  <Select label="Field" value={f.field} onChange={(e) => updateFilter(idx, { field: e.target.value as string })}>
                    {ensureSavedFieldOption(catalog, f.field).map((fc) => (
                      <MenuItem key={fc.field} value={fc.field}>{fc.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 140 }}>
                  <InputLabel>Op</InputLabel>
                  <Select label="Op" value={f.op} onChange={(e) => updateFilter(idx, { op: e.target.value as FilterOp })}>
                    {meta.operators.map((o) => (
                      <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {opMeta && opMeta.arity !== 'none' && (
                  <FilterValuePicker
                    tenantId={tenantId}
                    siteId={siteId}
                    field={f.field}
                    arity={opMeta.arity}
                    value={f.value}
                    onChange={(v) => updateFilter(idx, { value: v })}
                  />
                )}
                <IconButton color="error" size="small" onClick={() => removeFilter(idx)}>
                  <Delete fontSize="small" />
                </IconButton>
              </Box>
            );
          })
        )}
      </Box>
    </Box>
  );
};

const collectDimensionValueSuggestions = (draft: ReportDefinition): string[] => {
  const custom = (draft.visualizations?.[0]?.options as { dimensionValueLabels?: Record<string, string> } | undefined)
    ?.dimensionValueLabels || {};
  const dimField = draft.query.dimensions?.[0]?.field;
  const values = new Set<string>(Object.keys(custom).map((k) => k.trim()).filter(Boolean));
  for (const f of draft.query.filters || []) {
    if (!dimField || f.field !== dimField) continue;
    if (Array.isArray(f.value)) {
      f.value.forEach((v) => {
        const s = String(v).trim();
        if (s) values.add(s);
      });
    } else if (f.value !== undefined && f.value !== null && String(f.value).trim() !== '') {
      values.add(String(f.value).trim());
    }
  }
  return [...values];
};

/**
 * Pie / donut / bar legend text. Maps a raw ClickHouse dimension value
 * (e.g. `lucidlink_asset_migration_skipped`) to the caption shown next to
 * the slice — "Skipped 100%" today comes from a built-in pretty-print, and
 * this editor lets each panel override it.
 */
const DimensionValueLabelsEditor: React.FC<{
  draft: ReportDefinition;
  vis: { options?: Record<string, any> };
  updateVis: (patch: { options: Record<string, any> }) => void;
}> = ({ draft, vis, updateVis }) => {
  const labels = (vis.options?.dimensionValueLabels || {}) as Record<string, string>;
  const suggested = collectDimensionValueSuggestions(draft);
  const suggestedSet = new Set(suggested);
  const [extraRows, setExtraRows] = useState<Array<{ value: string; label: string }>>([]);
  const dimField = draft.query.dimensions?.[0]?.field || 'dimension';
  const extraVisible = extraRows
    .map((row, extraIdx) => ({ ...row, extraIdx }))
    .filter((row) => !row.value.trim() || !suggestedSet.has(row.value.trim()));
  const rows: Array<{ value: string; label: string; extraIdx?: number; lockedValue?: boolean }> = [
    ...suggested.map((value) => ({ value, label: labels[value] || '', lockedValue: true })),
    ...extraVisible,
  ];

  const writeLabels = (next: Record<string, string>) => {
    const cleaned: Record<string, string> = {};
    Object.entries(next).forEach(([k, v]) => {
      const key = k.trim();
      const val = String(v || '').trim();
      if (key && val) cleaned[key] = val;
    });
    const options = { ...(vis.options || {}) };
    if (Object.keys(cleaned).length === 0) {
      delete options.dimensionValueLabels;
    } else {
      options.dimensionValueLabels = cleaned;
    }
    updateVis({ options });
  };

  return (
    <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Slice / legend labels</Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
        Override the text shown on pie, donut, and bar legends for each raw
        {' '}{dimField} value. The percentage is appended automatically
        (for example, label <em>Unchanged</em> renders as “Unchanged 100%”).
        Leave a label blank to keep the default pretty name.
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {rows.map((row, idx) => (
          <Box key={`legend-row-${idx}`} sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField
              size="small"
              label="Raw value"
              sx={{ flex: '1 1 240px' }}
              placeholder="lucidlink_asset_migration_skipped"
              value={row.value}
              InputProps={{ readOnly: Boolean(row.lockedValue) }}
              onChange={(e) => {
                if (row.lockedValue || row.extraIdx === undefined) return;
                const nextValue = e.target.value;
                setExtraRows((cur) => cur.map((r, i) => (i === row.extraIdx ? { ...r, value: nextValue } : r)));
                const next = { ...labels };
                if (row.value) delete next[row.value];
                if (nextValue.trim() && row.label.trim()) next[nextValue.trim()] = row.label.trim();
                writeLabels(next);
              }}
            />
            <TextField
              size="small"
              label="Legend label"
              sx={{ flex: '1 1 160px' }}
              placeholder="e.g. Skipped"
              value={row.label}
              onChange={(e) => {
                const nextLabel = e.target.value;
                if (row.extraIdx !== undefined) {
                  setExtraRows((cur) => cur.map((r, i) => (i === row.extraIdx ? { ...r, label: nextLabel } : r)));
                }
                const key = row.value.trim();
                const next = { ...labels };
                if (!key) return;
                if (nextLabel.trim()) next[key] = nextLabel.trim();
                else delete next[key];
                writeLabels(next);
              }}
            />
            <IconButton
              color="error"
              size="small"
              aria-label="Remove legend label"
              onClick={() => {
                if (row.extraIdx !== undefined) {
                  setExtraRows((cur) => cur.filter((_, i) => i !== row.extraIdx));
                }
                if (row.value) {
                  const next = { ...labels };
                  delete next[row.value];
                  writeLabels(next);
                }
              }}
            >
              <Delete fontSize="small" />
            </IconButton>
          </Box>
        ))}
      </Box>
      <Button
        size="small"
        startIcon={<Add />}
        sx={{ mt: 1 }}
        onClick={() => setExtraRows((cur) => [...cur, { value: '', label: '' }])}
      >
        Add label
      </Button>
    </Box>
  );
};

// ─── Step 4: Visualization ────────────────────────────────────────────────

const VisualizationStep: React.FC<StepWithCatalogProps & { reports: ReportDefinition[] }> = ({ draft, setDraft, reports, catalog, meta }) => {
  const vis = draft.visualizations?.[0] || { type: 'line' };

  const updateVis = (patch: Partial<typeof vis>) => setDraft((d) => ({
    ...d,
    visualizations: [{ ...vis, ...patch }],
  }));

  const syncDamFolderPathFilter = (folderPath: string) => {
    setDraft((d) => {
      const hasParentPathFilter = (d.query.filters || []).some(
        (f) => f.field === 'custom_dimensions.folder_parent_path',
      );
      // Program-level DAM rollups filter on folder_parent_path (starts_with).
      // Do not replace that with an exact folder_path match.
      if (hasParentPathFilter) return d;
      const filters = (d.query.filters || []).filter((f) => f.field !== 'custom_dimensions.folder_path');
      const trimmed = folderPath.trim();
      if (trimmed) {
        filters.push({ field: 'custom_dimensions.folder_path', op: 'eq', value: trimmed });
      }
      return { ...d, query: { ...d.query, filters } };
    });
  };

  const syncDamFolderQuotasFilter = (folderQuotas: DamFolderQuotaEntry[]) => {
    setDraft((d) => {
      const filters = (d.query.filters || []).filter((f) => f.field !== 'custom_dimensions.folder_path');
      const paths = folderQuotas.map((q) => q.path.trim()).filter(Boolean);
      if (paths.length) {
        filters.push({ field: 'custom_dimensions.folder_path', op: 'in', value: paths });
      }
      return { ...d, query: { ...d.query, filters } };
    });
  };

  const applyStorageQuotaStackedBarPreset = (folderQuotas: DamFolderQuotaEntry[]) => {
    const normalized = folderQuotas
      .map((q) => ({ path: q.path.trim(), maxStorageTb: q.maxStorageTb }))
      .filter((q) => q.path);
    const paths = normalized.map((q) => q.path);
    setDraft((d) => {
      const otherFilters = (d.query.filters || []).filter(
        (f) => !['event_type', 'event_subtype', 'custom_dimensions.folder_path'].includes(f.field),
      );
      const filters = [
        ...otherFilters,
        { field: 'event_type', op: 'eq' as const, value: 'asset_disk_usage_report' },
        { field: 'event_subtype', op: 'eq' as const, value: 'dam_disk_usage_folder_snapshot' },
        ...(paths.length ? [{ field: 'custom_dimensions.folder_path', op: 'in' as const, value: paths }] : []),
      ];
      return {
        ...d,
        query: {
          ...d.query,
          metrics: withSnapshotCountMetric([
            { field: 'custom_metrics.size_mb_cumulative', fn: 'argMax', label: 'Storage' },
            { field: 'custom_metrics.asset_count_cumulative', fn: 'argMax', label: 'Assets' },
          ]),
          dimensions: [{ field: 'custom_dimensions.folder_path', label: 'DAM folder' }],
          filters,
          timeRange: {
            ...(d.query.timeRange || {}),
            preset: d.query.timeRange?.preset || 'last_7_days',
            granularity: undefined,
          },
          orderBy: [{ field: 'metric:0', dir: 'DESC' }],
        },
        visualizations: [{
          ...vis,
          type: 'storage_quota_stacked_bar',
          title: vis.title || 'DAM multi-folder storage',
          options: {
            ...(vis.options || {}),
            damFolderQuotas: normalized,
            valueFormat: 'decimal_mb',
          },
        }],
      };
    });
  };

  const applyStorageQuotaBarPreset = (folderPath: string, maxStorageTb: number) => {
    const trimmed = folderPath.trim();
    setDraft((d) => {
      const parentPathFilters = (d.query.filters || []).filter(
        (f) => f.field === 'custom_dimensions.folder_parent_path',
      );
      const otherFilters = (d.query.filters || []).filter(
        (f) => !['event_type', 'event_subtype', 'custom_dimensions.folder_path', 'custom_dimensions.folder_parent_path'].includes(f.field),
      );
      const filters = [
        ...otherFilters,
        { field: 'event_type', op: 'eq' as const, value: 'asset_disk_usage_report' },
        { field: 'event_subtype', op: 'eq' as const, value: 'dam_disk_usage_folder_snapshot' },
        ...(parentPathFilters.length
          ? parentPathFilters
          : (trimmed ? [{ field: 'custom_dimensions.folder_path', op: 'eq' as const, value: trimmed }] : [])),
      ];
      return {
        ...d,
        query: {
          ...d.query,
          metrics: withSnapshotCountMetric([
            { field: 'custom_metrics.size_mb_cumulative', fn: 'argMax', label: 'Storage' },
            { field: 'custom_metrics.asset_count_cumulative', fn: 'argMax', label: 'Assets' },
          ]),
          dimensions: d.query.dimensions?.length ? d.query.dimensions : [],
          filters,
          timeRange: {
            ...(d.query.timeRange || {}),
            preset: d.query.timeRange?.preset || 'last_7_days',
            granularity: undefined,
          },
          orderBy: undefined,
        },
        visualizations: [{
          ...vis,
          type: 'storage_quota_bar',
          title: vis.title || 'DAM folder storage',
          options: {
            ...(vis.options || {}),
            damFolderPath: trimmed,
            maxStorageTb,
            valueFormat: 'decimal_mb',
          },
        }],
      };
    });
  };

  const handleVisTypeChange = (nextType: VisualizationType) => {
    if (nextType === 'storage_quota_bar') {
      const folderPath = String((vis.options as { damFolderPath?: string } | undefined)?.damFolderPath || '/content/dam/');
      const maxStorageTb = Number((vis.options as { maxStorageTb?: number } | undefined)?.maxStorageTb) || 10;
      applyStorageQuotaBarPreset(folderPath, maxStorageTb);
      return;
    }
    if (nextType === 'storage_quota_stacked_bar') {
      const quotas = (vis.options as { damFolderQuotas?: DamFolderQuotaEntry[] } | undefined)?.damFolderQuotas;
      applyStorageQuotaStackedBarPreset(quotas?.length ? quotas : DEFAULT_DAM_FOLDER_QUOTAS);
      return;
    }
    updateVis({ type: nextType });
  };

  const interactions = draft.interactions || {};
  const onRowClick = interactions.onRowClick;
  const onPanelClick = interactions.onPanelClick;
  const availableDrilldowns = reports.filter((r) => r.mode === 'drilldown' && r._id !== draft._id);
  const isCounterOrNumber = vis.type === 'counter' || vis.type === 'number';
  const isStorageQuotaBar = vis.type === 'storage_quota_bar';
  const isStorageQuotaStackedBar = vis.type === 'storage_quota_stacked_bar';
  const isTable = vis.type === 'table';
  // The metadata's `kind` lets us decide which extra controls to render
  // without enumerating viz values everywhere — e.g. "show realtime knobs
  // when kind === 'realtime'".
  const visMeta = meta.visualizations.find((v) => v.value === vis.type);
  const isRealtime = visMeta?.kind === 'realtime' || vis.type === 'realtime' || vis.type === 'realtime_bar';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      <FormControl size="small" fullWidth>
        <InputLabel>Visualization Type</InputLabel>
        <Select
          label="Visualization Type"
          value={vis.type}
          onChange={(e) => handleVisTypeChange(e.target.value as VisualizationType)}
        >
          {meta.visualizations.map((v) => (
            <MenuItem key={v.value} value={v.value}>{v.label}</MenuItem>
          ))}
        </Select>
      </FormControl>

      <TextField
        size="small" label="Chart title"
        fullWidth
        value={vis.title || ''}
        onChange={(e) => updateVis({ title: e.target.value })}
      />

      {/*
        Realtime-only options. The bucket size doubles as the polling
        cadence (the panel refetches every `bucketSeconds`), so the value
        controls both how granular the live line is AND how often it
        moves. Defaults match the brief: 10-second buckets.

        These options apply to both `realtime` (line) and `realtime_bar`
        (bar) — the same RealtimePanel component renders both, just with
        a different chart shape baked into the type.
      */}
      {isRealtime && (
        <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Realtime panel options</Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            {/* The chart-type override is only useful when the visualization
                type is the generic `realtime` (line by default). When the
                user picks "Realtime Bar (live users)" we already know the
                shape from the type, so we hide this dropdown to avoid two
                competing controls saying different things. */}
            {vis.type === 'realtime' && (
              <FormControl size="small" sx={{ minWidth: 200, flex: 1 }}>
                <InputLabel>Chart type</InputLabel>
                <Select
                  label="Chart type"
                  value={String((vis.options as any)?.chartType || 'line')}
                  onChange={(e) => {
                    const v = String(e.target.value || 'line');
                    const next = v === 'bar' ? 'bar' : 'line';
                    updateVis({ options: { ...(vis.options || {}), chartType: next } });
                  }}
                >
                  <MenuItem value="line">Line</MenuItem>
                  <MenuItem value="bar">Bar</MenuItem>
                </Select>
              </FormControl>
            )}
            <TextField
              size="small"
              type="number"
              label="Poll cadence (seconds)"
              value={Number((vis.options as any)?.bucketSeconds ?? 10)}
              onChange={(e) => {
                const n = parseInt(e.target.value, 10);
                const v = Number.isFinite(n) ? Math.min(Math.max(n, 1), 3600) : 10;
                updateVis({ options: { ...(vis.options || {}), bucketSeconds: v } });
              }}
              inputProps={{ min: 1, max: 3600, step: 1 }}
              helperText="Poll cadence + default bucket width. 10 = update every 10s."
              sx={{ minWidth: 220, flex: 1 }}
            />
            <TextField
              size="small"
              type="number"
              label="Chart window (seconds)"
              value={Number((vis.options as any)?.windowSeconds ?? 0)}
              onChange={(e) => {
                const n = parseInt(e.target.value, 10);
                const v = Number.isFinite(n) ? Math.min(Math.max(n, 0), 86400) : 0;
                updateVis({ options: { ...(vis.options || {}), windowSeconds: v } });
              }}
              inputProps={{ min: 0, max: 86400, step: 60 }}
              helperText="X-axis lookback. 1800 = last 30 min. 0 = auto from panel width."
              sx={{ minWidth: 220, flex: 1 }}
            />
            <TextField
              size="small"
              type="number"
              label="Headline window (seconds)"
              value={Number((vis.options as any)?.summaryWindowSeconds ?? 900)}
              onChange={(e) => {
                const n = parseInt(e.target.value, 10);
                const v = Number.isFinite(n) ? Math.min(Math.max(n, 1), 86400) : 900;
                updateVis({ options: { ...(vis.options || {}), summaryWindowSeconds: v } });
              }}
              inputProps={{ min: 1, max: 86400, step: 1 }}
              helperText="Big number = active in last N seconds. 900 = 15 min."
              sx={{ minWidth: 220, flex: 1 }}
            />
            <FormControl size="small" sx={{ minWidth: 220, flex: 1 }}>
              <InputLabel>Headline counts</InputLabel>
              <Select
                label="Headline counts"
                value={String((vis.options as any)?.summaryIdField || 'session_id')}
                onChange={(e) => {
                  const v = String(e.target.value || 'session_id');
                  const allowed = new Set(meta.identityFields.map((f) => f.value));
                  const next = (allowed.has(v as any) ? v : 'session_id');
                  updateVis({ options: { ...(vis.options || {}), summaryIdField: next } });
                }}
              >
                {meta.identityFields.map((f) => (
                  <MenuItem key={f.value} value={f.value}>{f.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              size="small"
              label="Headline caption"
              value={String((vis.options as any)?.summaryCaption || '')}
              onChange={(e) => updateVis({
                options: { ...(vis.options || {}), summaryCaption: e.target.value },
              })}
              helperText="Optional. Overrides the generated “active <id>s in the last N” caption. Leave blank to keep the default."
              sx={{ minWidth: 280, flex: 1 }}
            />
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            The headline number reflects "active &lt;id&gt;s in the last N seconds" and is
            computed independently of the chart's bucket grid — so the live count stays
            meaningful even when the most recent bucket happens to be empty. The number
            of buckets shown on the x-axis is computed at runtime from the panel's
            rendered width — wider screens show more time intervals automatically.
          </Typography>
        </Box>
      )}

      {/* Counter / number tiles only count a single metric — show a hint
          to the user so they understand only the first metric is used. */}
      {(vis.type === 'counter' || vis.type === 'number') && (
        <Typography variant="caption" color="text.secondary">
          Tip: only the <strong>first metric</strong> from the Metrics step is shown — pick the one
          that represents the headline number you want to display.
        </Typography>
      )}

      {vis.type === 'counter' && (
        <FormControl size="small" sx={{ minWidth: 220, maxWidth: 360 }}>
          <InputLabel>Counter icon</InputLabel>
          <Select
            label="Counter icon"
            value={String((vis.options as { icon?: string } | undefined)?.icon || 'auto')}
            onChange={(e) => updateVis({
              options: { ...(vis.options || {}), icon: String(e.target.value) },
            })}
          >
            {COUNTER_ICON_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {/* Interactive distribution charts (pie/donut with the filterable folder
          table) can render as pie, donut, or a horizontal bar. This sets the
          initial view; viewers can still switch in-panel. */}
      {(vis.type === 'pie' || vis.type === 'donut')
        && Boolean((vis.options as { interactiveLegend?: boolean } | undefined)?.interactiveLegend) && (
        <Box sx={{ maxWidth: 360 }}>
          <FormControl size="small" fullWidth>
            <InputLabel>Default chart view</InputLabel>
            <Select
              label="Default chart view"
              value={String((vis.options as { defaultChartView?: string } | undefined)?.defaultChartView || vis.type)}
              onChange={(e) => updateVis({
                options: { ...(vis.options || {}), defaultChartView: String(e.target.value) },
              })}
            >
              <MenuItem value="pie">Pie Chart</MenuItem>
              <MenuItem value="donut">Donut Chart</MenuItem>
              <MenuItem value="bar">Horizontal-bar Chart</MenuItem>
            </Select>
          </FormControl>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            Initial view when the panel loads — viewers can still toggle pie / donut / bar.
          </Typography>
        </Box>
      )}

      {(vis.type === 'pie' || vis.type === 'donut' || vis.type === 'bar') && (
        <DimensionValueLabelsEditor draft={draft} vis={vis} updateVis={updateVis} />
      )}

      {isStorageQuotaStackedBar && (
        <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
          <Typography variant="subtitle2" sx={{ mb: 1.5 }}>Multi-folder DAM storage quota options</Typography>
          <FormControlLabel
            sx={{ mb: 1 }}
            control={(
              <Checkbox
                size="small"
                checked={Boolean((vis.options as { showBarPercentage?: boolean } | undefined)?.showBarPercentage)}
                onChange={(e) => updateVis({
                  options: { ...(vis.options || {}), showBarPercentage: e.target.checked },
                })}
              />
            )}
            label="Show aggregate percentage on the combined horizontal bar"
          />
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {((vis.options as { damFolderQuotas?: DamFolderQuotaEntry[] } | undefined)?.damFolderQuotas || []).map((entry, idx) => (
              <Box key={`${idx}-${entry.path}`} sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                <TextField
                  size="small"
                  label="DAM folder path"
                  fullWidth
                  sx={{ flex: '1 1 280px' }}
                  placeholder="/content/dam/px/arc"
                  value={entry.path}
                  onChange={(e) => {
                    const damFolderQuotas = [
                      ...(((vis.options as { damFolderQuotas?: DamFolderQuotaEntry[] })?.damFolderQuotas) || []),
                    ];
                    damFolderQuotas[idx] = { ...damFolderQuotas[idx], path: e.target.value };
                    updateVis({ options: { ...(vis.options || {}), damFolderQuotas } });
                    syncDamFolderQuotasFilter(damFolderQuotas);
                  }}
                />
                <TextField
                  size="small"
                  type="number"
                  label="Max (TB)"
                  placeholder="Auto"
                  value={entry.maxStorageTb ?? ''}
                  onChange={(e) => {
                    const raw = e.target.value.trim();
                    const maxStorageTb = raw === ''
                      ? undefined
                      : (Number.isFinite(parseFloat(raw)) && parseFloat(raw) > 0 ? parseFloat(raw) : undefined);
                    const damFolderQuotas = [
                      ...(((vis.options as { damFolderQuotas?: DamFolderQuotaEntry[] })?.damFolderQuotas) || []),
                    ];
                    damFolderQuotas[idx] = { ...damFolderQuotas[idx], maxStorageTb };
                    updateVis({ options: { ...(vis.options || {}), damFolderQuotas } });
                  }}
                  inputProps={{ min: 0.001, step: 0.1 }}
                  helperText="Leave empty to use current usage as max"
                  sx={{ width: 150 }}
                />
                <IconButton
                  size="small"
                  aria-label="Remove folder"
                  onClick={() => {
                    const damFolderQuotas = (
                      ((vis.options as { damFolderQuotas?: DamFolderQuotaEntry[] })?.damFolderQuotas) || []
                    ).filter((_, i) => i !== idx);
                    updateVis({ options: { ...(vis.options || {}), damFolderQuotas } });
                    syncDamFolderQuotasFilter(damFolderQuotas);
                  }}
                  disabled={(((vis.options as { damFolderQuotas?: DamFolderQuotaEntry[] })?.damFolderQuotas) || []).length <= 1}
                >
                  <Delete fontSize="small" />
                </IconButton>
              </Box>
            ))}
            <Button
              size="small"
              startIcon={<Add />}
              onClick={() => {
                const damFolderQuotas = [
                  ...(((vis.options as { damFolderQuotas?: DamFolderQuotaEntry[] })?.damFolderQuotas) || []),
                  { path: '/content/dam/' },
                ];
                updateVis({ options: { ...(vis.options || {}), damFolderQuotas } });
                syncDamFolderQuotasFilter(damFolderQuotas);
              }}
              sx={{ alignSelf: 'flex-start' }}
            >
              Add DAM folder
            </Button>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
            Bar width = sum of max capacities for selected folders (or current usage when max is not set).
            Colored segments = used storage per folder.
            Query: argMax per folder on custom_metrics.size_mb_cumulative (latest snapshot in range).
          </Typography>
        </Box>
      )}

      {isStorageQuotaBar && (
        <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
          <Typography variant="subtitle2" sx={{ mb: 1.5 }}>DAM storage quota options</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              size="small"
              label="DAM folder path"
              fullWidth
              placeholder="/content/dam/px/arc"
              value={String((vis.options as { damFolderPath?: string } | undefined)?.damFolderPath || '')}
              onChange={(e) => {
                const folderPath = e.target.value;
                updateVis({ options: { ...(vis.options || {}), damFolderPath: folderPath } });
                syncDamFolderPathFilter(folderPath);
              }}
              helperText="JCR path whose cumulative storage is shown. Syncs to query filter custom_dimensions.folder_path unless the query already uses folder_parent_path (program-level rollup)."
            />
            <TextField
              size="small"
              type="number"
              label="Maximum storage (TB)"
              value={Number((vis.options as { maxStorageTb?: number } | undefined)?.maxStorageTb ?? 10)}
              onChange={(e) => {
                const n = parseFloat(e.target.value);
                const maxStorageTb = Number.isFinite(n) && n > 0 ? n : 10;
                updateVis({ options: { ...(vis.options || {}), maxStorageTb } });
              }}
              inputProps={{ min: 0.001, step: 0.1 }}
              helperText="Quota cap for color thresholds: green &lt; 70%, amber 70–95%, red 95–100%, glow red &gt; 100%."
              sx={{ maxWidth: 280 }}
            />
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
            Query metrics: argMax custom_metrics.size_mb_cumulative and argMax
            custom_metrics.asset_count_cumulative (values from the latest snapshot in the time range).
            If the saved query groups by site and/or folder, the bar sums those latest rows.
          </Typography>
        </Box>
      )}

      <Divider>Drill-down (optional)</Divider>

      {(isCounterOrNumber || isStorageQuotaBar) && (
        <>
          <Typography variant="body2" color="text.secondary">
            For <strong>counter</strong>, <strong>number</strong>, and <strong>DAM Storage Quota Bar</strong> panels,
            clicking the tile opens the selected drill-down report inline on the Reports page
            (same view, below the toolbar).
          </Typography>
          <FormControl size="small" sx={{ minWidth: 220, maxWidth: 480 }}>
            <InputLabel>Target drill-down report (panel click)</InputLabel>
            <Select
              label="Target drill-down report (panel click)"
              value={onPanelClick?.targetReportName || ''}
              onChange={(e) => setDraft((d) => ({
                ...d,
                interactions: {
                  ...(d.interactions || {}),
                  onPanelClick: e.target.value
                    ? {
                        targetReportName: e.target.value as string,
                        level2ReportNames: onPanelClick?.level2ReportNames,
                      }
                    : undefined,
                },
              }))}
            >
              <MenuItem value="">— none —</MenuItem>
              {availableDrilldowns.map((r) => (
                <MenuItem key={r._id} value={r.name}>{r.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </>
      )}

      {isTable && (
        <>
          <Typography variant="body2" color="text.secondary">
            For <strong>table</strong> visualizations, clicking a row can open a drill-down report filtered by a
            specific dimension value (e.g. click a user row → user activity report).
          </Typography>

          <Box sx={{ display: 'flex', gap: 1, flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
            <FormControl size="small" sx={{ flex: 1, minWidth: 220 }}>
              <InputLabel>Target drill-down report (row click)</InputLabel>
              <Select
                label="Target drill-down report (row click)"
                value={onRowClick?.targetReportName || ''}
                onChange={(e) => setDraft((d) => ({
                  ...d,
                  interactions: {
                    ...(d.interactions || {}),
                    onRowClick: e.target.value
                      ? { targetReportName: e.target.value as string, passField: onRowClick?.passField || 'user_id' }
                      : undefined,
                  },
                }))}
              >
                <MenuItem value="">— none —</MenuItem>
                {availableDrilldowns.map((r) => (
                  <MenuItem key={r._id} value={r.name}>{r.name}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ flex: 1, minWidth: 200 }} disabled={!onRowClick?.targetReportName}>
              <InputLabel>Pass field as filter</InputLabel>
              <Select
                label="Pass field as filter"
                value={onRowClick?.passField || 'user_id'}
                onChange={(e) => setDraft((d) => ({
                  ...d,
                  interactions: {
                    ...(d.interactions || {}),
                    onRowClick: {
                      targetReportName: onRowClick?.targetReportName || '',
                      passField: e.target.value as string,
                    },
                  },
                }))}
              >
                {ensureSavedFieldOption(
                  catalog.filter((f) => f.allowedForDimension),
                  onRowClick?.passField,
                ).map((f) => (
                  <MenuItem key={f.field} value={f.field}>{f.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </>
      )}

      {!isCounterOrNumber && !isTable && (
        <Typography variant="body2" color="text.secondary">
          Drill-down linking is available for <strong>counter</strong>, <strong>number</strong>, and{' '}
          <strong>table</strong> visualizations. Choose one of those types to link this panel to a
          drill-down report.
        </Typography>
      )}
    </Box>
  );
};
